import { Icon } from "@iconify/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  BankDetails,
  Breadcrumbs,
  Button,
  DeleteModal,
  EditButton,
  EditProjectForm,
  FileUploadModal,
  FormWrapper,
  ImageUploadModal,
  LinkUploadModal,
  NewPhaseForm,
  ProjectPhase,
  SubmitButton,
  Table,
  ViewMediaModal,
} from "../../components";
import AddMediaModal from "../../components/modals/AddMediaModal";
import ImageSliderModal from "../../components/modals/ImageSliderModal";
import { emiOptions, FileTypes, UploadFileTypes } from "../../constants";
import { useBank, useProject } from "../../hooks";
import { parseAmount } from "../../services/utils";
import styles from "./ProjectListing.module.css";

const ProjectListingPage = () => {
  const navigate = useNavigate();
  const columnHeaders = [
    { id: "project_name", name: "Project Name" },
    { id: "company_name", name: "Company Name" },
    { id: "number_of_phases", name: "Number of Phases" },
    { id: "number_of_plots", name: "Number of plots" },
    { id: "total_plots_sold", name: "Plots Sold" },
    { id: "action", name: "More Details" },
  ];

  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [fileUploadOptions, setFileUploadOptions] = useState({});
  const [showLinkUploadModal, setShowLinkUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    fetchAllProjects,
    addNewPhase,
    fetchProjectById,
    deleteProjectById,
    getProjectMediaById,
    updateProjectById,
    addNewAmenity,
    addNewStore,
  } = useProject();
  const { updateBankDetails, addBankAccount } = useBank();

  const [projectList, setProjectList] = useState([]);
  useEffect(() => {
    const fetchProjectListing = async () => {
      const { projects, error } = await fetchAllProjects();
      if (error) return toast.error(error);
      setProjectList(projects);
    };
    fetchProjectListing();
    const isEditFormVisible = localStorage.getItem("edit_project_data");
    if (isEditFormVisible) setShowEditProjectForm(true);
  }, []);

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectBankDetails, setProjectBankDetails] = useState({});
  const [aurusCommissionBankDetails, setAurusCommissionBankDetails] = useState({});
  const [aurusRegistrationBankDetails, setAurusRegistrationBankDetails] = useState({});
  const [aurusDevelopmentBankDetails, setAurusDevelopmentBankDetails] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNewPhaseForm, setShowNewPhaseForm] = useState(false);
  const [showEditProjectForm, setShowEditProjectForm] = useState(false);
  const [showAddMediaModal, setShowAddMediaModal] = useState(false);

  useEffect(() => {
    if (searchParams.get("id") !== null) setSelectedProjectId(searchParams.get("id"));
    else {
      setSelectedProjectId(null);
      setSelectedProject(null);
    }
  }, []);

  const [newPhaseProjectData, setNewPhaseProjectData] = useState({});
  const fetchProject = async () => {
    const { project, error } = await fetchProjectById(selectedProjectId);
    if (error) return toast.error(error);
    const { media, error: mediaError } = await getProjectMediaById(selectedProjectId);
    if (mediaError) return toast.error(mediaError);

    setSelectedProject({ ...project.project, media });
    setProjectData({ ...project.project, media });
    setProjectBankDetails(project.projectBankDetails);
    setAurusCommissionBankDetails(project.aurusCommissionBankDetails);
    setAurusRegistrationBankDetails(project.registrationBankDetails);
    setAurusDevelopmentBankDetails(project.developmentBankDetails);
    setNewPhaseProjectData({
      emi: project.project.emi_options,
      token_percent: project.project.token_percent,
      development_charges: project.project.development_charges,
      registration_charges: project.project.registration_charges,
      start_range: project.project.start_range,
      end_range: project.project.end_range,
      phase_name: `Phase ${project.project.phases.length + 1}`,
      plot_area_max: project.project.plot_area_max,
    });
    if (detailsRef.current) detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (selectedProjectId && !showNewPhaseForm && !showEditProjectForm) {
      fetchProject();
    }
  }, [selectedProjectId, showNewPhaseForm, showEditProjectForm]);

  const [projectData, setProjectData] = useState({});
  const handleInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
    if (useTextCase === "uppercase") newValue = value.toUpperCase();

    setProjectData({ ...projectData, [name]: newValue });
  };

  const handleAddPhase = async (phaseData) => {
    const newPhaseData = { ...phaseData, phase_number: selectedProject.phases.length + 1, status: "open" };

    const { success, error } = await addNewPhase(selectedProjectId, newPhaseData);
    if (error) return toast.error(error);
    toast.success(success);
    setShowNewPhaseForm(false);
    fetchProject();
  };

  const updateProject = async (data) => {
    const { success, error } = await updateProjectById(selectedProjectId, data);
    if (error) return toast.error(error);
    toast.success(success);
    fetchProject();
  };

  const [editedDescription, setEditedDescription] = useState("");
  const handleDescriptionEdit = async () => {
    await updateProject({ description: editedDescription });
    setShowDescriptionEditForm(false);
  };
  const handleDescriptionInputChange = (e) => {
    setEditedDescription(e.target.value);
    setSelectedProject((prevData) => ({ ...prevData, description: e.target.value }));
  };

  const handleFileUpload = async (id, url, key) => {
    if (!id) return;
    await updateProject({ [key]: id });
    setShowImageUploadModal(false);
    setShowFileUploadModal(false);
    // setShowImageSliderModal(false);
  };

  const handleProjectLayoutUpload = () => {
    setFileUploadOptions({
      title: "Upload Project Layout",
      handleUpload: (id, url) => handleFileUpload(id, url, "project_layout"),
    });
    setShowImageUploadModal(true);
  };
  const [showImageSliderModal, setShowImageSliderModal] = useState(false);
  const handleImageSliderUpload = () => {
    setShowImageSliderModal(true);
    // setShowImageUploadModal(true);
  };
  const handleDroneVideoUpload = () => {
    setFileUploadOptions({
      title: "Upload Drone Video",
      handleUpload: (id, url) => handleFileUpload(id, url, "drone_video"),
      fileType: FileTypes.DRONE_VIDEO,
    });
    setShowImageUploadModal(true);
  };
  const handleBrochureUpload = () => {
    setFileUploadOptions({
      title: "Upload Brochure",
      handleUpload: (id, url) => handleFileUpload(id, url, "brochure"),
      fileType: FileTypes.BROCHURE,
    });
    setShowFileUploadModal(true);
  };
  const handleLocationUpload = async (link) => {
    await updateProject({ google_maps_url: link });
    setShowLinkUploadModal(false);
  };
  const handleMasterfileUpload = () => {
    setFileUploadOptions({
      title: "Upload Masterfile",
      handleUpload: (id, url) => handleFileUpload(id, url, "masterfile_pdf"),
      fileType: FileTypes.MASTERFILE,
    });
    setShowFileUploadModal(true);
  };
  const handleReraCertificateUpload = () => {
    setFileUploadOptions({
      title: "Upload RERA Certificate",
      handleUpload: (id, url) => handleFileUpload(id, url, "rera_certificate"),
      fileType: FileTypes.RERA_CERTIFICATE,
    });
    setShowFileUploadModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(false);
    const { success, error } = deleteProjectById(selectedProjectId);
    if (error) return toast.error(error);
    setSearchParams({});
    window.location.reload();
    toast.info(success);
  };

  const [showViewMediaModal, setShowViewMediaModal] = useState(false);
  const [viewMediaOptions, setViewMediaOptions] = useState({});

  const handleViewMedia = (title, mediaType, key) => {
    const url = selectedProject[key];
    setViewMediaOptions({ title: title, url: key === "slider_images" ? url : [url], mediaType: mediaType });
    setShowViewMediaModal(true);
  };

  const handleViewProjectMedia = (title, mediaType, url) => {
    setViewMediaOptions({ title: title, url: [url], mediaType: mediaType });
    setShowViewMediaModal(true);
  };

  useEffect(() => {
    const getProjectMedia = async () => {
      const { media, error } = await getProjectMediaById(selectedProjectId);
      if (error) return toast.error(error);
      if (media.length > 0) {
        setSelectedProject({ ...selectedProject, media });
      }
    };
    getProjectMedia();
  }, []);

  const [showDescriptionEditForm, setShowDescriptionEditForm] = useState(false);

  const [newAmenity, setNewAmenity] = useState({ amenity_name: "", amenity_icon: "", icon_url: "" });
  const [showAmenitiesEditForm, setShowAmenitiesEditForm] = useState(false);
  const handleAddAmenity = (e) => {
    e.preventDefault();
    if (!newAmenity.amenity_name || newAmenity.amenity_name === "") {
      return toast.error("Amenity name is required");
    }
    if (!newAmenity.amenity_icon || !newAmenity.icon_url) {
      return toast.error("Amenity icon is required");
    }
    addAmenity();
  };

  const addAmenity = async () => {
    const { success, error } = await addNewAmenity({
      project_id: selectedProjectId,
      amenities: [newAmenity],
    });
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(success);
    setShowAmenitiesEditForm(false);
    await fetchProject();
  };
  const handleNewAmenityIconUpload = () => {
    setShowImageUploadModal(true);
    setFileUploadOptions({
      title: "Upload Amenity Icon",
      handleUpload: (id, url) => setNewAmenity((prev) => ({ ...prev, amenity_icon: id, icon_url: url })),
      fileType: UploadFileTypes.IMAGES,
    });
  };

  const [newStore, setNewStore] = useState({ store_name: "", distance: "", icon: "", icon_url: "" });
  const [showStoresEditForm, setShowStoresEditForm] = useState(false);
  const handleAddStore = (e) => {
    e.preventDefault();
    if (!newStore.store_name || newStore.store_name === "") {
      return toast.error("Store name is required");
    }
    if (!newStore.icon || !newStore.icon_url) {
      return toast.error("Store icon is required");
    }
    if (!newStore.distance || newStore.distance < 0) {
      return toast.error("Store distance is required");
    }
    addStore();
  };

  const addStore = async () => {
    const { success, error } = await addNewStore({
      project_id: selectedProjectId,
      stores: [newStore],
    });
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(success);
    setShowStoresEditForm(false);
    await fetchProject();
  };

  const handleNewStoreIconUpload = () => {
    setShowImageUploadModal(true);
    setFileUploadOptions({
      title: "Upload Store Icon",
      handleUpload: (id, url) => setNewStore((prev) => ({ ...prev, icon: id, icon_url: url })),
      fileType: UploadFileTypes.IMAGES,
    });
  };

  const handleUpdateBankDetails = async (bankId, editedData) => {
    const dataToUpdate = { ...editedData };
    if (Object.keys(editedData).includes("bank_branch")) {
      dataToUpdate.bank_branch_name = editedData.bank_branch;
    }
    const { success, error } = await updateBankDetails(bankId, dataToUpdate);
    if (error) return toast.error(error);
    toast.success(success);
    fetchProject();
  };

  const handleAddBankDetails = async (editedData, key) => {
    const newData = {
      ...editedData,
      bank_branch_name: editedData.bank_branch,
      bank_identification_id: editedData.bank_identification,
    };
    const { success, error } = await addBankAccount(key, selectedProjectId, newData);
    if (error) {
      toast.error(error);
    }
    toast.success(success);
    await fetchProject();
  };

  const handleProjectBankDetailsEdit = (editedData) => {
    const bankId = projectBankDetails.id;
    handleUpdateBankDetails(bankId, editedData);
  };
  const handleAurusCommissionBankDetailsEdit = (editedData) => {
    const bankId = aurusCommissionBankDetails.id;
    handleUpdateBankDetails(bankId, editedData);
  };
  const handleDevelopmentBankDetailsEdit = (editedData) => {
    const bankId = aurusDevelopmentBankDetails.id;
    handleUpdateBankDetails(bankId, editedData);
  };
  const handleRegistrationBankDetailsEdit = (editedData) => {
    const bankId = aurusRegistrationBankDetails.id;
    handleUpdateBankDetails(bankId, editedData);
  };

  const [selectedMedia, setSelectedMedia] = useState(null);
  const handleMediaUploadButtonClick = (media) => {
    setShowAddMediaModal(true);
    setSelectedMedia(media);
  };

  const handleCloseAddMediaModal = async () => {
    setShowAddMediaModal(false);
    setSelectedMedia(null);
    await fetchProject();
  };

  const phaseRef = useRef();
  const detailsRef = useRef();
  useEffect(() => {
    if (selectedProjectId && detailsRef.current)
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // after navigating from plot page, scroll to phases
  useEffect(() => {
    if (searchParams.get("from") === "plot" && selectedProjectId) {
      document.getElementById("phases")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });

  const handleEditProject = async (isEdited) => {
    if (isEdited) await fetchProject();
    setShowEditProjectForm(false);
  };

  return (
    <>
      <Breadcrumbs />
      <Button className="cta" end onClick={() => navigate("/project-listing/add-new-project")}>
        Add New Project
      </Button>
      <Table columns={columnHeaders} data={projectList} title="Total Projects" setSelectedId={setSelectedProjectId} />

      {selectedProjectId && selectedProject && (
        <main className="section_wrapper" ref={detailsRef} id="project-details">
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">{selectedProject.project_name}</h2>
              <Button
                className="ghost_no_underline"
                icon="basil:add-outline"
                iconPosition="start"
                onClick={() => {
                  // on click scroll to the phase form using ref
                  phaseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  localStorage.removeItem("new_phase_data");
                  setShowNewPhaseForm(true);
                }}
              >
                Add Phase
              </Button>
              <Button
                className="ghost_no_underline"
                icon="material-symbols-light:delete-outline"
                iconPosition="start"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
              <EditButton iconPosition="start" onClick={() => setShowEditProjectForm(true)} />
            </section>
            <section className="row start">
              <div className={styles.logo_wrapper}>
                {selectedProject.company_logo_url ? (
                  <Button
                    className="ghost"
                    onClick={() => handleViewMedia("Company Logo", "image", "company_logo_url")}
                  >
                    <img src={selectedProject.company_logo_url} className={styles.logo_img} alt="project logo" />
                  </Button>
                ) : (
                  "Logo"
                )}
              </div>
              <p>{selectedProject.project_name}</p>
            </section>
            {showEditProjectForm ? (
              <EditProjectForm onClose={handleEditProject} data={selectedProject} />
            ) : (
              <section className="section grid_3">
                <div className="column spread">
                  <div className="row">
                    <span className="label">Company Name</span>
                    <span className="value">{selectedProject.company_name}</span>
                  </div>
                  <div className="row">
                    <span className="label">State</span>
                    <span className="value">{selectedProject.state}</span>
                  </div>
                  <div className="row">
                    <span className="label">City</span>
                    <span className="value">{selectedProject.city}</span>
                  </div>
                  <div className="row">
                    <span className="label">Gaon</span>
                    <span className="value">{selectedProject.gaon}</span>
                  </div>
                  <div className="row">
                    <span className="label">Taluka</span>
                    <span className="value">{selectedProject.taluka}</span>
                  </div>
                  <div className="grid_3">
                    <span className="value">{selectedProject.plot_area_min} sq.ft</span>
                    <span className="label is-center">Plot Area Range</span>
                    <span className="value row end">{selectedProject.plot_area_max} sq.ft</span>
                  </div>
                  <div className="row">
                    <span className="label">Short Location</span>
                    <span className="value">{selectedProject.short_location}</span>
                  </div>
                  <div className="row">
                    <span className="label">Zone</span>
                    <span className="value">{selectedProject.zone}</span>
                  </div>
                  <div className="row">
                    <span className="label">Road To Plot</span>
                    <span className="value">{selectedProject.road_to_plot}</span>
                  </div>
                  {/* <div className="row">
                    <span className="label">Direction (Vaastu)</span>
                    <span className="value">{selectedProject.direction}</span>
                  </div> */}
                  {/* <div className="row">
                    <span className="label">Text Written Location</span>
                    <span className="value">{selectedProject.text_location}</span>
                  </div> */}
                  <div className="row">
                    <span className="label">Google maps location</span>
                    <Link target="_blank" to={selectedProject.google_maps_url} className="preview_dark">
                      link
                    </Link>
                  </div>
                </div>
                <div className="vertical_divider"></div>
                <div className="column spread">
                  <div className="row">
                    <span className="label">Sanctioning authority</span>
                    <input
                      type="text"
                      name="sanctioning_authority"
                      value={projectData.sanctioning_authority}
                      className="input full"
                      onChange={(e) => handleInputChange(e, "uppercase")}
                      pattern="([a-zA-Z\s+]+)"
                      title="Only alphabets are allowed"
                      readOnly
                      maxLength={50}
                    />
                  </div>
                  <div className="row">
                    <span className="label">Type</span>
                    <span className="value">{selectedProject.project_type}</span>
                  </div>
                  <div className="row rate_range">
                    <div className="input_with_prefix_wrapper">
                      <span className="prefix">₹</span>
                      <input
                        type="number"
                        value={projectData.start_range}
                        className="input full"
                        onChange={handleInputChange}
                        min={0}
                        max={projectData.end_range}
                        readOnly
                      />
                    </div>
                    <span className="label">Rate Range</span>
                    <div className="input_with_prefix_wrapper">
                      <span className="prefix">₹</span>
                      <input
                        type="number"
                        value={projectData.end_range}
                        className="input full"
                        onChange={handleInputChange}
                        min={projectData.start_range}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="row">
                    <span className="label">EMI options</span>
                    <Select
                      name="emi_options"
                      id="emi_options"
                      value={emiOptions.find((option) => option.value === `${selectedProject.emi_options}`)}
                      className="select"
                      options={emiOptions}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1px solid var(--gray-light)",
                          boxShadow: "none",
                          ":hover": {
                            border: "1px solid var(--gray-light)",
                          },
                          backgroundColor: "#fff",
                        }),
                        singleValue: (baseStyle) => ({ ...baseStyle, color: "var(--color-body-text)" }),
                      }}
                      isDisabled
                    />
                  </div>
                  <div className="row">
                    <span className="label">Token Percent</span>
                    <span className="value">{selectedProject.token_percent.replace(".00", "")}%</span>
                  </div>
                  <div className="row">
                    <span className="label">Maha RERA number</span>
                    <span className="value">{selectedProject.rera_number}</span>
                  </div>
                  <div className="row">
                    <span className="label">
                      Development Charges <br /> (Water & Electricity)
                    </span>
                    <span className="value">{parseAmount(selectedProject.development_charges)}</span>
                  </div>
                  <div className="row">
                    <span className="label">
                      Registration Charges <br /> + Advocate fees
                    </span>
                    <span className="value">{parseAmount(selectedProject.registration_charges)}</span>
                  </div>
                  <div className="row">
                    <span className="label">Project completion ETA</span>
                    <span className="value">{selectedProject.completion_eta}</span>
                  </div>
                  <div className="row">
                    <span className="label">Limit number of plots agent can put on priority</span>
                    <span className="value">{selectedProject.plot_hold_limit} plots</span>
                  </div>
                </div>
              </section>
            )}
          </section>
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Gallery</h2>
              <Button className="ghost_no_underline" onClick={() => setShowAddMediaModal(true)}>
                + Add new media
              </Button>
            </section>
            <div className="column">
              <div className="row">
                <span className="label">Project Layout</span>
                {selectedProject.project_layout_url ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Project Layout", "image", "project_layout_url")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No project layout uploaded</span>
                )}
                <Button className="ghost_gray" onClick={handleProjectLayoutUpload}>
                  upload
                </Button>
              </div>
              <div className="row">
                <span className="label">Image Slider</span>
                {selectedProject.slider_images.length > 0 ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Image Slider", "image", "slider_images")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No image slider uploaded</span>
                )}
                <Button className="ghost_gray" onClick={handleImageSliderUpload}>
                  upload
                </Button>
              </div>
              <div className="row">
                <span className="label">Drone Video</span>
                {selectedProject.drone_video_url ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Drone Video", "video", "drone_video_url")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No drone video uploaded</span>
                )}
                <Button className="ghost_gray" onClick={handleDroneVideoUpload}>
                  upload
                </Button>
              </div>
              {selectedProject.media && selectedProject.media.length > 0
                ? selectedProject.media.map((media) => (
                    <div key={media.id} className="row">
                      <span className="label">{media.media_title}</span>
                      <Button
                        className="ghost_gray"
                        onClick={() => handleViewProjectMedia(media.media_title, media.media_type, media.url)}
                      >
                        preview
                      </Button>
                      <Button className="ghost_gray" onClick={() => handleMediaUploadButtonClick(media)}>
                        upload
                      </Button>
                    </div>
                  ))
                : ""}
            </div>
          </section>
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Why invest in this project?</h2>
              <EditButton onClick={() => setShowDescriptionEditForm(true)} />
            </section>
            <div className="column">
              {showDescriptionEditForm ? (
                <div className="column">
                  <div className="input_wrapper">
                    <textarea
                      className="input_textarea"
                      value={selectedProject.description}
                      onChange={(e) => handleDescriptionInputChange(e)}
                      maxLength={600}
                    />
                    <span className="end small gray">{selectedProject.description?.length ?? 0}/600</span>
                  </div>
                  <div className="row end">
                    <Button className="ghost_outline_round" onClick={() => setShowDescriptionEditForm(false)}>
                      Cancel
                    </Button>
                    <Button className="cta" onClick={handleDescriptionEdit}>
                      Update
                    </Button>
                  </div>
                </div>
              ) : (
                <pre>{selectedProject.description}</pre>
              )}
            </div>
          </section>
          <section className="section_wrapper">
            <section className="section_title_wrapper section grid_3">
              <div className="row">
                <h2 className="section_title">Amenities</h2>
                <EditButton onClick={() => setShowAmenitiesEditForm(true)} />
              </div>
              <div className="vertical_divider"></div>
              <div className="row">
                <h2 className="section_title">Nearby Stores</h2>
                <EditButton onClick={() => setShowStoresEditForm(true)} />
              </div>
            </section>
            <div className="section grid_3">
              <div className="column">
                {showAmenitiesEditForm && (
                  <FormWrapper form="new-amenity-form" onSubmit={handleAddAmenity}>
                    <div className="column">
                      <div className="row">
                        <input
                          className="input full"
                          type="text"
                          required
                          placeholder="Enter Amenity"
                          onChange={(e) =>
                            setNewAmenity({
                              ...newAmenity,
                              amenity_name: e.target.value.replace(
                                /\w\S*/g,
                                (txt) => txt.charAt(0).toUpperCase() + txt.slice(1)
                              ),
                            })
                          }
                          pattern="(^[A-Za-z_@./#&+\-]*$)"
                          title="Only alphabets, special characters are allowed"
                          value={newAmenity.amenity_name}
                          maxLength={20}
                        />
                        {newAmenity.icon_url && newAmenity.amenity_icon ? (
                          <Button className="ghost_gray" onClick={() => handleViewMedia("new")}>
                            preview
                          </Button>
                        ) : (
                          <span className="small gray">No Icon Uploaded</span>
                        )}
                        <Button className="upload" onClick={handleNewAmenityIconUpload} end>
                          Upload
                        </Button>
                      </div>

                      <div className="row end">
                        <Button className="ghost_outline_round" onClick={() => setShowAmenitiesEditForm(false)}>
                          Cancel
                        </Button>
                        <SubmitButton onClick={handleAddAmenity} form="new-amenity-form" className="cta">
                          Add
                        </SubmitButton>
                      </div>
                    </div>
                  </FormWrapper>
                )}
                {selectedProject.amenities && selectedProject.amenities.length > 0 ? (
                  <div className="grid_2 amenities_grid">
                    {selectedProject.amenities.map((amenity) => (
                      <div key={amenity.id} className="row">
                        {amenity.icon_url ? (
                          <img src={amenity.icon_url} alt={amenity.name} className="icon_img" />
                        ) : (
                          <Icon icon={amenity.icon ?? "material-symbols-light:deck-outline"} inline />
                        )}
                        <span>{amenity.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="label">No amenities added.</span>
                )}
              </div>
              <div className="vertical_divider"></div>
              <div className="column">
                {showStoresEditForm && (
                  <FormWrapper id="new-store-form" onSubmit={handleAddStore}>
                    <div className="column">
                      <div className="row">
                        <input
                          className="input full"
                          type="text"
                          required
                          placeholder="Enter Store"
                          onChange={(e) =>
                            setNewStore({
                              ...newStore,
                              store_name: e.target.value.replace(
                                /\w\S*/g,
                                (txt) => txt.charAt(0).toUpperCase() + txt.slice(1)
                              ),
                            })
                          }
                          pattern="(^[A-Za-z_@./#&+\-]*$)"
                          title="Only alphabets, special characters are allowed"
                          maxLength={20}
                        />
                        <div className="input_with_suffix_wrapper shrink">
                          <input
                            className="input"
                            type="number"
                            step={0.1}
                            required
                            placeholder="Enter Distance"
                            onChange={(e) =>
                              setNewStore({
                                ...newStore,
                                distance: e.target.value,
                              })
                            }
                            min={0}
                            max={50}
                          />
                          <span className="suffix">km</span>
                        </div>
                        {newStore.icon_url && newStore.store_icon ? (
                          <Button className="ghost_gray" onClick={() => handleViewMedia("new")}>
                            preview
                          </Button>
                        ) : (
                          <span className="small gray">No Icon Uploaded</span>
                        )}
                        <Button className="upload" onClick={handleNewStoreIconUpload} end>
                          Upload
                        </Button>
                      </div>

                      <div className="row end">
                        <Button className="ghost_outline_round" onClick={() => setShowStoresEditForm(false)}>
                          Cancel
                        </Button>
                        <SubmitButton form="new-store-form" className="cta">
                          Add
                        </SubmitButton>
                      </div>
                    </div>
                  </FormWrapper>
                )}
                {selectedProject.nearby_stores && selectedProject.nearby_stores.length > 0 ? (
                  selectedProject.nearby_stores.map((store) => (
                    <div key={store.id} className="row">
                      <div className="full row start">
                        {store.icon_url ? (
                          <img src={store.icon_url} alt="" className="icon_img" />
                        ) : (
                          <Icon icon={store.icon ?? "lets-icons:road-alt"} inline />
                        )}
                        <span>{store.name}</span>
                      </div>
                      <div className="small">{store.distance} km</div>
                    </div>
                  ))
                ) : (
                  <span className="label">No nearby stores added.</span>
                )}
              </div>
            </div>
          </section>
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Related Documents</h2>
            </section>
            <section className="section grid_3">
              <div className="column start">
                <div className="row">
                  <div className="label">Brochure</div>
                  {selectedProject.brochure?.url ? (
                    <Link target="_blank" to={selectedProject.brochure.url} className="preview">
                      view
                    </Link>
                  ) : (
                    <span className="small gray">No brochure uploaded</span>
                  )}
                  <Button className="ghost" onClick={handleBrochureUpload}>
                    upload
                  </Button>
                </div>
                <div className="row">
                  <div className="label">Project Layout</div>
                  {selectedProject.project_layout_url ? (
                    <Button
                      className="ghost_gray"
                      onClick={() => handleViewMedia("Project Layout", "image", "project_layout_url")}
                    >
                      preview
                    </Button>
                  ) : (
                    <span className="small gray">No project layout uploaded</span>
                  )}
                  <Button className="ghost" onClick={handleProjectLayoutUpload}>
                    upload
                  </Button>
                </div>
                <div className="row">
                  <div className="label">Location</div>
                  {selectedProject.google_maps_url ? (
                    <Link target="_blank" to={selectedProject.google_maps_url} className="preview">
                      visit link
                    </Link>
                  ) : (
                    <span className="small gray">No location link uploaded</span>
                  )}
                  <Button className="ghost" onClick={() => setShowLinkUploadModal(true)}>
                    upload link
                  </Button>
                </div>
              </div>
              <div className="vertical_divider"></div>
              <div className="column start">
                <div className="row">
                  <div className="label">Masterfile PDF</div>
                  {selectedProject.masterfile_pdf?.url ? (
                    <Link target="_blank" to={selectedProject.masterfile_pdf.url} className="preview">
                      view
                    </Link>
                  ) : (
                    <span className="small gray">No masterfile uploaded</span>
                  )}
                  <Button className="ghost" onClick={handleMasterfileUpload}>
                    upload
                  </Button>
                </div>
                <div className="row">
                  <div className="label">RERA Certificate</div>
                  {selectedProject.rera_certificate?.url ? (
                    <Link target="_blank" to={selectedProject.rera_certificate.url} className="preview">
                      view
                    </Link>
                  ) : (
                    <span className="small gray">No RERA certificate uploaded</span>
                  )}
                  <Button className="ghost" onClick={handleReraCertificateUpload}>
                    upload
                  </Button>
                </div>
              </div>
            </section>
          </section>
          <section className="section_wrapper">
            <section className="section_title_wrapper" id="phases" ref={phaseRef}>
              <h2 className="section_title">Phase List</h2>
            </section>
            {showNewPhaseForm && (
              <NewPhaseForm
                handleSubmit={handleAddPhase}
                handleCancel={() => setShowNewPhaseForm(false)}
                projectData={newPhaseProjectData}
              />
            )}
            {selectedProject.phases && selectedProject.phases.length > 0 ? (
              selectedProject.phases.map((phase) => (
                <ProjectPhase
                  data={{ ...phase, project_id: selectedProjectId }}
                  key={phase.id}
                  projectData={newPhaseProjectData}
                />
              ))
            ) : (
              <span className="label">No phases added.</span>
            )}
          </section>
          <BankDetails
            parentName="Project"
            title={`${selectedProject.project_name} Bank Details`}
            showEditButton
            handleEdit={handleProjectBankDetailsEdit}
            id="project"
            data={projectBankDetails}
            handleIdentificationUpload={(imageId, url) =>
              handleProjectBankDetailsEdit({ bank_identification_id: imageId })
            }
            handleAdd={(data) => handleAddBankDetails(data, "project_bank_details")}
          />
          <BankDetails
            parentName="Company"
            title="Aurus Group Bank Details"
            titleSmall="(for commission)"
            handleEdit={handleAurusCommissionBankDetailsEdit}
            showEditButton
            id="aurus_commission"
            data={aurusCommissionBankDetails}
            handleIdentificationUpload={(imageId, url) =>
              handleAurusCommissionBankDetailsEdit({ bank_identification_id: imageId })
            }
            handleAdd={(data) => handleAddBankDetails(data, "aurus_commission_bank_details")}
          />
          <BankDetails
            parentName="Company"
            title="Bank Account Details for Development Charges"
            // titleSmall="(for development charges)"
            handleEdit={handleDevelopmentBankDetailsEdit}
            showEditButton
            id="aurus_development"
            data={aurusDevelopmentBankDetails}
            handleIdentificationUpload={(imageId, url) =>
              handleDevelopmentBankDetailsEdit({ bank_identification_id: imageId })
            }
            handleAdd={(data) => handleAddBankDetails(data, "development_bank_details")}
          />
          <BankDetails
            parentName="Company"
            title="Bank Account Details for Registration Charges"
            // titleSmall="(for registration charges)"
            handleEdit={handleRegistrationBankDetailsEdit}
            showEditButton
            id="aurus_registration"
            handleIdentificationUpload={(imageId, url) =>
              handleRegistrationBankDetailsEdit({ bank_identification_id: imageId })
            }
            data={aurusRegistrationBankDetails}
            handleAdd={(data) => handleAddBankDetails(data, "registration_bank_details")}
          />
          {showImageUploadModal &&
            createPortal(
              <ImageUploadModal
                {...fileUploadOptions}
                isVisible={showImageUploadModal}
                handleClose={() => setShowImageUploadModal(false)}
              />,
              document.body
            )}
          {showFileUploadModal &&
            createPortal(
              <FileUploadModal
                {...fileUploadOptions}
                isVisible={showFileUploadModal}
                handleClose={() => setShowFileUploadModal(false)}
              />,
              document.body
            )}
          {showLinkUploadModal &&
            createPortal(
              <LinkUploadModal
                handleSave={handleLocationUpload}
                isVisible={showLinkUploadModal}
                handleClose={() => setShowLinkUploadModal(false)}
                linkType="google_maps_url"
                invalidMessage="Please enter a valid google maps url"
              />,
              document.body
            )}
          {showDeleteModal &&
            createPortal(
              <DeleteModal
                isVisible={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDelete={handleDelete}
                message="Are you sure you want to delete this project?"
              />,
              document.body
            )}
          {showViewMediaModal &&
            createPortal(
              <ViewMediaModal
                handleClose={() => setShowViewMediaModal(false)}
                isModalVisible={showViewMediaModal}
                {...viewMediaOptions}
              />,
              document.body
            )}

          {showAddMediaModal &&
            createPortal(
              <AddMediaModal
                handleClose={handleCloseAddMediaModal}
                isModalVisible={showAddMediaModal}
                isUpdate={selectedMedia ? true : false}
                media={selectedMedia}
              />,
              document.body
            )}
          {showImageSliderModal &&
            createPortal(
              <ImageSliderModal
                handleClose={() => setShowImageSliderModal(false)}
                isModalVisible={showImageSliderModal}
                handleUpload={(data, url) => handleFileUpload(data, url, "slider_images")}
                // {...imageSliderOptions}
              />,
              document.body
            )}
        </main>
      )}
    </>
  );
};
export default ProjectListingPage;
